/**
 * KAN-149: frame ownership (one artist per frame ID, features/artist-statement.md).
 * Pure helpers shared by Photobooth Management (upload, frame settings) and Artist Management (assigned list, picker).
 * The frame ID is the per-image anchor stored in `uploadedFrames[].frameId`, the same ID the kiosk counts prints under in
 * `devices/{id}/reports/{yyyy-MM}/frames/{frameId}`. A frame with no artist is company-owned ("Unassigned (company)").
 */
import { matchFrameGroupTheme } from './standalone-housekeeping.util';

export const FRAME_ARTWORKS_COLLECTION = 'frame_artworks';

export const FRAME_OWNERSHIP_MESSAGES = {
  unassigned: 'Unassigned (company)',
  ownerDeleted: 'Owner deleted',
  inactive: 'Inactive artist',
  artistNotAssignable: 'This artist is inactive or deleted and cannot be assigned. Nothing was changed.',
  ownerChanged: 'This frame was changed by someone else. Reopen it and try again. Nothing was changed.',
  allCopies: 'Applies to every global and device copy of this frame.',
  artistsLoadFailed: 'Artists could not be loaded. The frame uploads as Unassigned (company); you can assign it later.'
} as const;

export interface FrameOwnership {
  frameId: string;
  artistId: string | null;
  fpImagePath?: string | null;
  isDeleted?: boolean;
  updatedAt?: unknown;
  updatedBy?: string;
}

export interface ArtistLite {
  id: string;
  name?: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

/** One draft frame-group document, read from `photobooth_config/{global|deviceId}/frame_groups/{themeId}_{groupId}`. */
export interface FrameGroupSource {
  scope: 'global' | 'device';
  deviceId: string | null;
  deviceName: string;
  groupDocId: string;
  groupName: string;
  knownThemeIds: string[];
  uploadedFrames: any[];
}

export interface FrameCopy {
  scope: 'global' | 'device';
  deviceId: string | null;
  deviceName: string;
  themeId: string;
  groupId: string;
  groupName: string;
}

export interface FrameCatalogEntry {
  frameId: string;
  fpName: string | null;
  fpImagePath: string | null;
  copies: FrameCopy[];
}

export type OwnerStateKind = 'unassigned' | 'this' | 'other' | 'ownerDeleted';

export interface OwnerState {
  kind: OwnerStateKind;
  artistId: string | null;
  artistName: string;
  inactive: boolean;
}

/** Firestore document id for a frame ID: `/` is not allowed in ids, everything else is kept as is. */
export function ownershipDocId(frameId: string): string {
  const id = String(frameId || '').trim();
  if (!id) throw new Error('A frame ID is required.');
  const safe = id.replace(/%/g, '%25').replace(/\//g, '%2F');
  return safe === '.' || safe === '..' ? encodeURIComponent(safe).replace(/\./g, '%2E') : safe;
}

/** Storage path of a frame's `_FP` image inside a draft frame group. */
export function fpImagePathFor(scope: 'global' | 'device', deviceId: string | null, themeId: string, groupId: string, fpName: string): string {
  const base = scope === 'global' ? 'global' : deviceId;
  return `Photobooth/${base}/Assets/${themeId}/${groupId}/${fpName}`;
}

/** Splits `{themeId}_{groupId}` with the longest known theme ID, falling back to the first underscore (as getAllFrameGroups). */
export function splitGroupDocId(groupDocId: string, knownThemeIds: string[]): { themeId: string; groupId: string } {
  const theme = matchFrameGroupTheme(groupDocId, knownThemeIds);
  if (theme) return { themeId: theme, groupId: groupDocId.substring(theme.length + 1) };
  const i = groupDocId.indexOf('_');
  return i === -1 ? { themeId: groupDocId, groupId: '' } : { themeId: groupDocId.substring(0, i), groupId: groupDocId.substring(i + 1) };
}

/**
 * One entry per frame ID across the global and every device draft frame group. Copies of one frame ID merge into one entry
 * (they share the owner). The image is the `_FP` file, preferring the global copy.
 */
export function buildFrameCatalog(groups: FrameGroupSource[]): FrameCatalogEntry[] {
  const byId = new Map<string, FrameCatalogEntry>();
  for (const g of groups) {
    const { themeId, groupId } = splitGroupDocId(g.groupDocId, g.knownThemeIds);
    for (const uf of g.uploadedFrames || []) {
      const frameId = uf && typeof uf === 'object' && typeof uf.frameId === 'string' ? uf.frameId.trim() : '';
      if (!frameId) continue;
      const fpName: string | null = typeof uf.fpName === 'string' && uf.fpName ? uf.fpName : null;
      const path = fpName && groupId ? fpImagePathFor(g.scope, g.deviceId, themeId, groupId, fpName) : null;
      const copy: FrameCopy = { scope: g.scope, deviceId: g.deviceId, deviceName: g.deviceName, themeId, groupId, groupName: g.groupName || groupId };
      const entry = byId.get(frameId);
      if (!entry) {
        byId.set(frameId, { frameId, fpName, fpImagePath: path, copies: [copy] });
        continue;
      }
      entry.copies.push(copy);
      if (path && (!entry.fpImagePath || g.scope === 'global')) {
        entry.fpImagePath = path;
        entry.fpName = fpName;
      }
    }
  }
  return [...byId.values()].sort((a, b) => a.frameId.localeCompare(b.frameId));
}

/** Active and not deleted. A missing `isActive` counts as active (artists created before the flag existed). */
export function isAssignableArtist(artist: ArtistLite | null | undefined): boolean {
  return !!artist && artist.isDeleted !== true && artist.isActive !== false;
}

/** Who owns a frame, seen from the artist being edited (`editingArtistId`, null outside Artist Management). */
export function ownerStateFor(
  frameId: string,
  ownerships: Map<string, FrameOwnership>,
  artistsById: Map<string, ArtistLite>,
  editingArtistId: string | null
): OwnerState {
  const record = ownerships.get(frameId);
  const artistId = record && record.isDeleted !== true ? record.artistId || null : null;
  if (!artistId) return { kind: 'unassigned', artistId: null, artistName: FRAME_OWNERSHIP_MESSAGES.unassigned, inactive: false };
  const artist = artistsById.get(artistId);
  if (!artist || artist.isDeleted === true) {
    return { kind: 'ownerDeleted', artistId, artistName: artist?.name || FRAME_OWNERSHIP_MESSAGES.ownerDeleted, inactive: false };
  }
  return {
    kind: artistId === editingArtistId ? 'this' : 'other',
    artistId,
    artistName: artist.name || artistId,
    inactive: artist.isActive === false
  };
}

/** Taking a frame needs a confirm only when another live artist owns it (a deleted owner's frame is taken without one). */
export function needsTakeConfirm(owner: OwnerState): boolean {
  return owner.kind === 'other';
}

export interface PickerOptions {
  showAll: boolean;
  search: string;
  scope: 'all' | 'global' | 'device';
}

export interface PickerRow {
  entry: FrameCatalogEntry;
  owner: OwnerState;
}

/**
 * Picker rows for the artist being edited: never the artist's own frames. "Unassigned only" (default) keeps company-owned frames
 * and frames whose owner was deleted; "Show all frames" adds frames owned by other artists (shown with an owner badge).
 */
export function filterPickerFrames(
  catalog: FrameCatalogEntry[],
  ownerships: Map<string, FrameOwnership>,
  artistsById: Map<string, ArtistLite>,
  editingArtistId: string,
  opts: PickerOptions
): PickerRow[] {
  const q = (opts.search || '').trim().toLowerCase();
  return catalog
    .map(entry => ({ entry, owner: ownerStateFor(entry.frameId, ownerships, artistsById, editingArtistId) }))
    .filter(row => row.owner.kind !== 'this')
    .filter(row => opts.showAll || row.owner.kind === 'unassigned' || row.owner.kind === 'ownerDeleted')
    .filter(row => opts.scope === 'all' || row.entry.copies.some(c => c.scope === opts.scope))
    .filter(row => !q || [row.entry.frameId, ...row.entry.copies.flatMap(c => [c.groupName, c.themeId, c.deviceName])]
      .some(v => (v || '').toLowerCase().includes(q)));
}

/** Frames owned by one artist, in catalog order. */
export function framesOwnedBy(catalog: FrameCatalogEntry[], ownerships: Map<string, FrameOwnership>, artistId: string): FrameCatalogEntry[] {
  return catalog.filter(e => {
    const r = ownerships.get(e.frameId);
    return !!r && r.isDeleted !== true && r.artistId === artistId;
  });
}

/** Short scope label for a copy, e.g. "Global" or the device name. */
export function scopeLabel(copy: FrameCopy): string {
  return copy.scope === 'global' ? 'Global' : (copy.deviceName || copy.deviceId || 'Device');
}
